export const siteConfig = {
  name: "Annota Studio",
  tagline: "Build · Innovate · Empower",
  mission: "Privacy-focused software for serious work.",
  email: "annotadesk@annotastudio.com",
  secondaryEmail: "support@annotastudio.com",
  customEmail: "custom@annotastudio.com",
  calculatorEmail: "annotacalc@annotastudio.com",
  calculatorUrl: "/tools/annotacalc/index.html",
  legacyEmails: ["annotadesk@gmail.com", "annotadesk@outlook.com"],
  siteUrl: "https://annotastudio.com",
  trial: {
    product: "AnnotaDesk",
    edition: "7-Day Trial",
    platform: "Windows",
    version: "v1.1.1",
    fileSize: "59.4 MB",
    updated: "27 August 2026",
    filename: "AnnotaDesk_v1.1.1_7_DAY_TRIAL.zip",
    checksum: "23F2B6818349C15618EA43594B566911173BFBDC7D92E24E81996C6B35B6C296",
    downloadUrl: "/downloads/AnnotaDesk_v1.1.1_7_DAY_TRIAL.zip",
  },
} as const;

export type Product = {
  slug: string;
  name: string;
  kicker: string;
  status: string;
  category: string;
  summary: string;
  audience: string;
  features: string[];
  disclaimer?: string;
};

export const products: Product[] = [
  {
    slug: "annotadesk",
    name: "AnnotaDesk",
    kicker: "PDF review and comment-register workflow",
    status: "Available now",
    category: "Document Intelligence",
    summary: "Extract annotations from client-marked PDFs, organise a structured review register, track replies and resolution, and export familiar office formats — with local desktop processing.",
    audience: "Engineers, EPC contractors, consultants, document controllers, QA/QC teams, project managers and review coordinators.",
    features: ["PDF-native comment extraction", "Page, author and annotation references", "Search, filters and structured review", "Reply, ownership and resolution fields", "Excel, Word and CSV exports", "Local desktop processing"],
  },
  {
    slug: "comparedesk",
    name: "CompareDesk",
    kicker: "Document comparison and revision intelligence",
    status: "Available now",
    category: "Document Intelligence",
    summary: "Compare document and P&ID revisions offline on Windows, review old and new evidence, use English OCR, add editable revision marks and export detailed reports.",
    audience: "Engineers, document controllers, QA/QC teams, project managers, consultants and vendor-document reviewers.",
    features: ["Document and visual P&ID comparison", "Offline English OCR", "Editable revision clouds and shapes", "Saved comments and review decisions", "Word, Excel, TXT, HTML and PDF reports", "Offline activation for each licensed PC"],
  },
  {
    slug: "offline-paraphraser",
    name: "Offline Paraphrasing Tool",
    kicker: "Private rewriting and voice-assisted drafting",
    status: "Upcoming",
    category: "Document Intelligence",
    summary: "Improve clarity, tone, grammar and structure without sending confidential drafts to a public cloud service.",
    audience: "Engineers, proposal teams, managers, students, technical writers and professionals handling sensitive drafts.",
    features: ["Professional and technical tones", "Shorten, expand and summarise", "Formatting preservation where feasible", "Compare and export results", "Local phrase libraries", "Future offline dictation"],
  },
  {
    slug: "project-commissioning-assistant",
    name: "Project & Commissioning Assistant",
    kicker: "Project execution workspace",
    status: "Upcoming",
    category: "Project & Field Execution",
    summary: "Organise engineering deliverables and commissioning activity from design review through FAT, SAT, testing, startup, punch closure and handover.",
    audience: "Project and commissioning engineers, EPC teams, OEMs, package vendors, site managers and client representatives.",
    features: ["Project and equipment registers", "Deliverable and action tracking", "FAT/SAT checklists", "Test and trial-run records", "Punch-list closure", "Handover summaries"],
  },
  {
    slug: "pid-narrator",
    name: "P&ID Narrator",
    kicker: "P&ID-to-process-description assistant",
    status: "Upcoming",
    category: "Engineering Utilities",
    summary: "Convert reviewed P&IDs and supporting information into a structured, equipment-wise process-description draft while keeping verification with the responsible engineer.",
    audience: "Process engineers and technical teams preparing operating and scope narratives.",
    features: ["System and equipment organisation", "Operating-sequence drafting", "Control and utility interface sections", "Unclear-tag review questions", "Source references", "Word-ready structured drafts"],
    disclaimer: "Drafting aid only. Every output must be checked against the latest approved project documents by a qualified engineer.",
  },
  {
    slug: "isoforge-studio",
    name: "IsoForge Studio",
    kicker: "Isometric workflow and fabrication-data assistant",
    status: "Upcoming",
    category: "Engineering Utilities",
    summary: "Organise piping and isometric information into clearer fabrication-data workflows without implying unverified automatic design.",
    audience: "Piping engineers, designers, fabricators, EPC teams, site engineers and QA/QC teams.",
    features: ["Structured line and material data", "Annotation and revision notes", "BOM/MTO preparation support", "Spool and weld references", "Controlled schedules", "Verification flags"],
  },
  {
    slug: "annotacommission",
    name: "AnnotaCommission",
    kicker: "Commissioning records and daily reporting",
    status: "Upcoming",
    category: "Project & Field Execution",
    summary: "Bring commissioning reports, readings, punch items, test records and daily updates into one structured local workspace.",
    audience: "Commissioning teams, project engineers, OEMs, site managers and equipment owners.",
    features: ["Daily progress reports", "Timestamped readings", "Equipment history", "Test and acceptance records", "Punch-list ownership", "Weekly and handover exports"],
  },
  {
    slug: "annotainspect",
    name: "AnnotaInspect",
    kicker: "Inspection, evidence and corrective-action management",
    status: "Upcoming",
    category: "Project & Field Execution",
    summary: "Record checks consistently, attach evidence, raise findings, assign actions and retain a traceable inspection history.",
    audience: "Inspection, quality, commissioning and field-execution teams.",
    features: ["Reusable checklists", "Measured-value fields", "Photographic evidence", "Finding classification", "Corrective-action closure", "PDF and Excel reports"],
    disclaimer: "Checklists support inspection work but do not replace governing codes, approved ITPs, competent-person judgement or client requirements.",
  },
  {
    slug: "annotacalc",
    name: "AnnotaCalc",
    kicker: "Engineering calculators and unit converter",
    status: "Available now",
    category: "Engineering Utilities",
    summary: "40 unit-conversion categories and 15 calculator areas for geometry, process equipment, piping and preliminary safety screening in one browser workspace.",
    audience: "Process, chemical, mechanical and plant engineers, students, educators and technical teams exploring preliminary design options.",
    features: ["40 unit-conversion categories", "2D and 3D geometry and vessel estimates", "Piping, line sizing and heater-duty tools", "Control valves, mixing, packed beds and insulation", "Preliminary thickness, flange and relief screening", "Light and dark themes with personal wallpapers"],
    disclaimer: "Results are preliminary engineering aids and must be independently verified against applicable codes, standards, vendor data and project requirements.",
  },
  {
    slug: "annotalog",
    name: "AnnotaLog",
    kicker: "Experiment, pilot-plant and equipment logbook",
    status: "Upcoming",
    category: "Project & Field Execution",
    summary: "Turn scattered experiment, pilot-plant, maintenance and shift notes into a searchable local operational history.",
    audience: "R&D engineers, pilot-plant teams, operators, laboratories, commissioning teams and equipment owners.",
    features: ["Timestamped log entries", "Run conditions and results", "Equipment events", "Deviation follow-up", "Attachments and references", "Searchable exports"],
  },
  {
    slug: "trading-journal",
    name: "Trading Journal",
    kicker: "Trading review, risk and performance workspace",
    status: "Upcoming",
    category: "Trading Tools",
    summary: "Record trades, review decisions, track risk and understand performance across symbols and accounts without promising profitable outcomes.",
    audience: "Self-directed traders who want a consistent, local-first review process.",
    features: ["Trade and risk records", "Clear P&L calculations", "Equity-curve views", "Filters, screenshots and tags", "CSV import and export", "Optional future read-only API sync"],
    disclaimer: "Educational and record-keeping software only; not financial advice and no guarantee of trading performance.",
  },
];

export const productCategories = ["Document Intelligence", "Project & Field Execution", "Engineering Utilities", "Trading Tools"];

export const routes = ["/", "/products", ...products.map((p) => `/products/${p.slug}`), "/custom-software", "/about", "/contact", "/privacy", "/license", "/trial-terms", "/thank-you"];

export const licenceMailto = `mailto:${siteConfig.email}?subject=${encodeURIComponent("AnnotaDesk Full-Version Licence Enquiry")}&body=${encodeURIComponent(`Hello Annota Studio,\nI have reviewed/tried AnnotaDesk and would like information about the full-version licence.\nName:\nCompany/Organization:\nRole:\nNumber of intended users/devices:\nIntended use:\nPreferred licence type (Individual/Team/Department/Organization/Not sure):\nSupport or customization required:\nQuestions:\nPlease share the available licence, activation, pricing, update, and support options.\nThank you.`)}`;

export const customMailto = `mailto:${siteConfig.secondaryEmail},${siteConfig.customEmail}?subject=${encodeURIComponent("Software & Website Services Enquiry — Annota Studio")}&body=${encodeURIComponent(`Hello Annota Studio,\nI would like to discuss software development, website development, or website maintenance.\nName:\nCompany/Organization:\nIndustry/Department:\nProject goals or maintenance needs:\nExisting website URL or current input files/data:\nExpected output:\nNumber of users:\nPreferred platform:\nOffline requirement:\nRequired timeline:\nIntegrations, reports, or licensing needs:\nAdditional questions:\nPlease let me know the next steps for a scope discussion.\nThank you.`)}`;
